import { useEffect, useState } from 'react';
import { Dialog, DialogTitle, DialogContent } from '@mui/material';
import { z } from 'zod';
import {
    FaUser,
    FaUndo,
    FaBox,
    FaCreditCard,
    FaAngleDown,
    FaBoxOpen,
    FaTruck,
    FaTimes,
    FaSave,
} from 'react-icons/fa';


import { FaEdit } from 'react-icons/fa';
import axios from 'axios';
import { useForm } from 'react-hook-form';
import { IoIosCloseCircle } from "react-icons/io";
import { zodResolver } from '@hookform/resolvers/zod';
import { useMyContext } from '../../../context/MyContext';
import { FaWarehouse } from "react-icons/fa6";
import '../../css/custom-scrollbar.css';
import { Base_Url, updateShipment } from '../../../config/config';
import { toast } from 'react-toastify';

const schema = z.object({
    order_id: z.string().min(1, { message: 'Order ID is required' }),
    first_name: z.string().min(1, { message: 'First name is required' }),
    last_name: z.string().min(1, { message: 'Last name is required' }),
    // address: z.string().min(1, { message: 'Address is required' }),
    address: z
        .string()
        .trim()
        .min(10, "Full Address must be at least 10 characters")
        .regex(/^[^#\\%&;]*$/, "Address contains invalid characters (# \\ % & ; not allowed)")
        .refine((val) => val.trim().length >= 10, {
            message: "Address cannot be only spaces",
        }),

    phone: z.string().min(1, { message: 'Phone is required'}),
    payment_mode: z.string().min(1, { message: 'Payment mode is required' }),
    cod_amount: z.string().min(1, { message: 'COD amount is required' }),
    length: z.number().positive({ message: 'Length must be positive' }),
    shipment_width: z.number().positive({ message: 'Width must be positive' }),
    shipment_height: z.number().positive({ message: 'Height must be positive' }),
    weight: z.number().positive({ message: 'weight must be positive' }),
    warehouse_id: z.string().min(1, { message: 'Warehouse name is required' })
});

const ViewAllShipment = ({ isopen, onClose, shipment, wareHouseList, fetchOrderData }) => {

    const { isDarkMode } = useMyContext();
    const [currentWarehouseName, setCurrentWarehouseName] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const token = localStorage.getItem('token');
    const cus_id = localStorage.getItem('cus_id');

    const { register, handleSubmit, formState: { errors }, reset, setValue, watch } = useForm({
        resolver: zodResolver(schema),
        defaultValues: {
            order_id: '',
            first_name: '',
            last_name: '',
            address: '',
            phone: '',
            payment_mode: '',
            cod_amount: '',
            length: '',
            shipment_width: '',
            shipment_height: '',
            weight: '',
            warehouse_id: ''
        }
    });

    const paymentMode = watch('payment_mode');

    // Check if a field is editable
    const isEditable = (fieldName) => {
        const editableFields = [
            'warehouse_id',
            'first_name', 'last_name', 'address',
            'phone', 'payment_mode', 'cod_amount', 'length',
            'shipment_width', 'shipment_height', 'weight',
        ];
        return editableFields.includes(fieldName) && isEditing;
    };

    useEffect(() => {
        if (shipment) {
            // Set basic form values
            setValue('order_id', shipment.order_id || '');
            setValue('first_name', shipment.first_name || '');
            setValue('last_name', shipment.last_name || '');
            setValue('address', shipment.address || '');
            setValue('phone', shipment.phone || '');
            setValue('payment_mode', shipment.payment_mode || '');
            setValue('cod_amount', shipment.cod_amount || shipment.cod || '');
            setValue('length', shipment.length || 0);
            setValue('shipment_width', shipment.shipment_width || 0);
            setValue('shipment_height', shipment.shipment_height || 0);
            setValue('weight', shipment.weight || 0);

            if (wareHouseList && wareHouseList.length > 0) {
                let selectedWarehouse = null;
                if (shipment.warehouse_id) {
                    selectedWarehouse = wareHouseList.find(w => w.value === shipment.warehouse_id);
                }
                if (selectedWarehouse) {
                    setValue('warehouse_id', selectedWarehouse.label);
                    setCurrentWarehouseName(selectedWarehouse.label); // Store for display
                } else {
                    setCurrentWarehouseName('Not specified');
                }
            }
        }
    }, [shipment, wareHouseList, setValue]);


    const onSubmit = async (data) => {
        const url = Base_Url + updateShipment;
        try {
            setIsLoading(true);
            setError(null);

            // Find the selected warehouse ID based on the warehouse name
            let selectedWarehouseId = shipment.warehouse_id;
            if (data.warehouse_id && wareHouseList) {
                const selectedWarehouse = wareHouseList.find(w => w.label === data.warehouse_id);
                if (selectedWarehouse) {
                    selectedWarehouseId = selectedWarehouse.value;
                }
            }
            const payload = {
                user_id: shipment.user_id, // Keep non-editable fields from shipment
                order_id: data.order_id,   // Use form data for editable fields
                customer: {
                    first_name: data.first_name,     // Updated
                    last_name: data.last_name,       // Updated
                    email: shipment.email,           // Keep original (not editable)
                    phone: data.phone,               // Updated
                    address_type: shipment.address_type, // Keep original (not editable)
                },
                droplocation: {
                    address: data.address,           // Updated
                    city: shipment.city,             // Keep original (not editable)
                    state: shipment.state,           // Keep original (not editable)
                    country: shipment.country,       // Keep original (not editable)
                    pin: shipment.pin,               // Keep original (not editable)
                },
                pickuplocation: {
                    warehouse_id: selectedWarehouseId, // Updated based on selected warehouse
                },
                product: {
                    product_desc: shipment.products_desc,    // Keep original (not editable)
                    quantity: shipment.quantity,             // Keep original (not editable)
                    total_amount: shipment.total_amount,     // Keep original (not editable)
                    cod_amount: data.cod_amount,             // Updated
                    payment_mode: data.payment_mode,         // Updated
                    shipment_type: shipment.shipment_type,   // Keep original (not editable)
                },
                dimension: {
                    length: data.length,                     // Updated
                    width: data.shipment_width,              // Updated
                    height: data.shipment_height,            // Updated
                    weight: parseInt(data.weight, 10)    // Keep original (not editable)
                },
            };

            if (data.payment_mode === 'prepaid') {
                delete payload.product.cod_amount;
            }

            // Call API to update shipment
            const response = await axios.post(url, payload, {
                headers: {
                    'Content-Type': 'application/json',
                    'token': token,
                    'cus_id': cus_id,
                },
            });

            if (response.data.status === "SUCCESS") {
                toast.success(response.data.message);
                setIsEditing(false);
                setIsLoading(false);
                onClose();
                fetchOrderData();
            }
        } catch (err) {
            const apiError = err.response?.data;
            if (apiError?.messages) {
                // Show all field-level errors
                Object.values(apiError.messages).forEach(msg => {
                    toast.error(msg);
                });
                setIsLoading(false);
                setError(apiError.messages);
            } else {
                // Fallback error
                const errorMessage = apiError?.message;
                toast.error(errorMessage);
                setError(errorMessage);
                setIsLoading(false);
            }
        }
    };

    const handleEdit = () => {
        setIsEditing(true);
    };


    const renderField = (label, fieldName, type = 'text', isNumeric = false) => {
        const editable = isEditable(fieldName);
        let value = shipment?.[fieldName] || '';

        if (fieldName === 'cod_amount' && !value && shipment?.cod) {
            value = shipment.cod;
        }

        if (fieldName === 'weight' && shipment?.weight) {
            value = shipment.weight;
        }
        return (
            <div>
                <label className="block text-xs mb-1 font-medium">{label}</label>
                {editable ? (
                    <>
                        <input
                            type={type}
                            step={isNumeric ? "0.1" : undefined}
                            placeholder={`Enter ${label}`}
                            className={`p-1.5 text-xs appearance-none shadow-md rounded-custom border w-full focus:outline-none focus:shadow-outline ${isDarkMode
                                    ? "bg-darkmodeColor border-gray-600 text-white placeholder-gray-500 focus:border-customPurple"
                                    : "bg-white border-gray-300 text-gray-800 placeholder-gray-400 focus:border-customPurple"
                                }`}
                            {...register(fieldName, isNumeric ? { valueAsNumber: true } : {})}
                        />
                        {errors[fieldName] && <p className="text-red-500 text-xs mt-1">{errors[fieldName].message}</p>}
                    </>
                ) : (
                    <div className={`p-1.5 text-xs appearance-none shadow-md rounded-custom border w-full focus:outline-none focus:shadow-outline ${isDarkMode
                            ? "border-gray-600 text-gray-100"
                            : "bg-gray-100 border-gray-300 text-gray-800"
                        }`}>
                        <span className="text-xs">{value || 'Not specified'}</span>
                    </div>
                )}
            </div>
        );
    };

    const handleClose = () => {
        setIsEditing(false); // Reset edit mode when closing
        onClose();
    };

    if (!shipment) return null;

    return (

        <Dialog
            open={isopen}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                className: "rounded-lg overflow-hidden"
            }}
        >
            <DialogTitle className="bg-gradient-to-r from-purple-600 to-indigo-700 text-white shadow-lg">
                <IoIosCloseCircle
                    className="absolute top-3 right-3 cursor-pointer hover:scale-110 transition-transform"
                    size={20}
                    onClick={handleClose}
                />
            </DialogTitle>


            <DialogContent className={`sm:mt-1 overflow-auto transition-all duration-300 custom-scrollbar rounded-custom md:mx-4 md:mb-4 ${isDarkMode
                ? "dark:bg-darkmodeColor dark:text-white shadow-custom-dark"
                : "bg-white"
                }`}>
                {/* <div className="p-6 bg-white dark:bg-darkmodeColor  shadow-md dark:shadow-custom-dark rounded-custom dark:text-gray-200"> */}
                {/* {error && (
                    <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-6 rounded">
                        <div className="flex items-center">
                            <svg className="h-5 w-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                            </svg>
                            {error}
                        </div>
                    </div>
                )} */}

                {!isEditing && (
                    <div className="flex justify-end mb-4">
                        <button
                            type="button"
                            onClick={handleEdit}
                            disabled={shipment?.status !== 'Pending'}
                            className={`py-2 px-4 rounded-lg mt-4 focus:outline-none focus:ring-2 focus:ring-opacity-50 font-medium transition-colors flex items-center ${shipment?.status === 'Pending'
                                ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white hover:from-blue-700 hover:to-blue-800 focus:ring-blue-500'
                                : 'bg-white text-gray-500 cursor-not-allowed'
                                }`}
                        >
                            <FaEdit className="mr-2" /> Edit
                        </button>
                    </div>
                )}

                <form className="space-y-8 font-sans" onSubmit={handleSubmit(onSubmit)}>

                    {/* Customer Information Section */}
                    <div className="bg-white dark:bg-darkmodeColor mt-4 p-3 rounded-custom shadow-md dark:shadow-custom-dark border-l-4 border-purple-500">

                        {/* Header */}
                        <h3 className="text-sm font-semibold mb-4 text-purple-700 dark:text-purple-300 flex items-center">
                            <FaUser className="mr-2" />
                            Customer Information
                        </h3>

                        {/* Warehouse + Order ID */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">

                            {/* Warehouse Name */}
                            <div>
                                <label className="block text-xs mb-1 font-medium">
                                    Warehouse Name
                                </label>

                                {isEditing ? (
                                    <>
                                        <select
                                            {...register('warehouse_id', {
                                                required: 'Warehouse Name is required'
                                            })}
                                            className={`p-1.5 text-xs appearance-none shadow-md rounded-custom border w-full focus:outline-none
                        ${isDarkMode
                                                    ? "dark:bg-darkmodeColor dark:text-white dark:border-gray-600"
                                                    : "bg-white"
                                                }`}
                                        >
                                            <option value="">Select Warehouse</option>
                                            {wareHouseList?.map((warehouse) => (
                                                <option
                                                    key={warehouse.value}
                                                    value={warehouse.label}
                                                >
                                                    {warehouse.label} - {warehouse.pincode}
                                                </option>
                                            ))}
                                        </select>

                                        {errors.warehouse_id && (
                                            <p className="text-red-500 text-sm mt-1">
                                                {errors.warehouse_id.message}
                                            </p>
                                        )}
                                    </>
                                ) : (
                                    <div
                                        className={`p-1.5 text-xs appearance-none shadow-md rounded-custom border w-full focus:outline-none
                    ${isDarkMode
                                                ? "dark:bg-darkmodeColor dark:text-white dark:border-gray-600"
                                                : "bg-white"
                                            }`}
                                    >
                                        <span className="font-medium text-xs">
                                            {currentWarehouseName || 'Not specified'}
                                        </span>
                                    </div>
                                )}
                            </div>

                            {/* Order ID */}
                            <div>
                                {renderField('Order ID', 'order_id')}
                            </div>
                        </div>

                        {/* Name */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-1">
                            <div>
                                {renderField('First Name', 'first_name')}
                            </div>
                            <div>
                                {renderField('Last Name', 'last_name')}
                            </div>
                        </div>

                        {/* Email + Phone */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-1">
                            <div>
                                {renderField('Email', 'email')}
                            </div>
                            <div>
                                {renderField('Phone', 'phone')}
                            </div>
                        </div>

                        {/* City / State / Pincode */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-1">
                            <div>
                                {renderField('City', 'city')}
                            </div>
                            <div>
                                {renderField('State', 'state')}
                            </div>
                        </div>

                        {/* Country */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-1">
                            <div>
                                {renderField('Pincode', 'pin')}
                            </div>
                            <div>
                                {renderField('Country', 'country')}
                            </div>
                        </div>

                        <div className="grid grid-cols-1 gap-6 mt-1">
                            <div>
                                {renderField('Address', 'address')}
                            </div>
                        </div>

                    </div>


                    {/* Return Address Section */}
                    <div className="dark:bg-darkmodeColor  shadow-md dark:shadow-custom-dark rounded-custom p-4  border-l-4 border-blue-500">
                        <h3 className="text-sm font-semibold mb-4 text-blue-700 dark:text-blue-300 flex items-center">
                            <FaUndo className="mr-2" />
                            Return Details
                        </h3>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className=" ">
                                {renderField('Return Address', 'return_address')}
                            </div>
                            <div className=" ">
                                {renderField('Return Phone', 'return_phone')}
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-1">
                            <div className=" ">
                                {renderField('Return City', 'return_city')}
                            </div>
                            <div className=" ">
                                {renderField('Return State', 'return_state')}
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-1">
                            <div className=" ">
                                {renderField('Return Pincode', 'return_pin')}
                            </div>
                            <div className=" ">
                                {renderField('Return Country', 'return_country')}
                            </div>
                        </div>
                    </div>

                    {/* Shipment Details Section */}
                    <div className="dark:bg-darkmodeColor shadow-md dark:shadow-custom-dark rounded-custom p-4 border-l-4 border-green-500">
                        <h3 className="text-sm font-semibold mb-2 text-green-700 dark:text-green-300 flex items-center">
                            <FaBox className="mr-2" />
                            Shipment Details
                        </h3>

                        {/* <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

                            <div className=" ">
                                {renderField('Shipment Type', 'shipment_type')}
                            </div>
                        </div> */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-1">
                            <div className=" ">
                                {renderField('Length', 'length', 'number', true)}
                            </div>
                            <div className=" ">
                                {renderField('Width', 'shipment_width', 'number', true)}
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-1">
                            <div className=" ">
                                {renderField('Height', 'shipment_height', 'number', true)}
                            </div>
                            <div className=" ">
                                {renderField('Weight (grams)', 'weight', 'number', true)}
                            </div>
                        </div>
                    </div>

                    {/* Payment Details Section */}
                    <div className=" dark:bg-darkmodeColor  shadow-md dark:shadow-custom-dark p-4 rounded-custom border-l-4 border-yellow-500">
                        <h3 className="text-sm font-semibold mb-4 text-yellow-700 dark:text-yellow-300 flex items-center">
                            <FaCreditCard className="mr-2" />
                            Payment Details
                        </h3>

                        {/* Payment Mode Field */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className=" ">
                                <label className="block text-xs mb-1 font-medium">Payment Mode</label>
                                {isEditing ? (
                                    <>
                                        <div className="relative">
                                            <select
                                                {...register('payment_mode')}
                                                className="p-1.5 text-xs appearance-none shadow-md rounded-custom border w-full focus:outline-none"
                                            >
                                                <option value="">Select Mode</option>
                                                <option value="prepaid">Prepaid</option>
                                                <option value="cod">COD</option>
                                            </select>
                                            <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                                                <FaAngleDown className="text-gray-500" />
                                            </div>
                                        </div>
                                        {errors.payment_mode && (
                                            <p className="text-red-500 text-sm mt-1">{errors.payment_mode.message}</p>
                                        )}
                                    </>
                                ) : (
                                    <div className={`p-1.5 text-xs appearance-none shadow-md rounded-custom border w-full focus:outline-none ${isDarkMode
                                        ? "bg-gradient-to-r from-customPurple to-purple-700 border-l-customPurple text-white"
                                        : ""
                                        }`}>
                                        <span className="block text-xs mb-1 font-medium">{shipment?.payment_mode || 'Not specified'}</span>
                                    </div>
                                )}
                            </div>

                            {/* COD Amount Field (Shown only if payment_mode is 'cod') */}
                            {paymentMode === 'cod' && (
                                <div className=" ">
                                    {renderField('COD Amount', 'cod_amount')}
                                </div>
                            )}
                        </div>
                    </div>


                    {/* Product Details Section */}
                    <div className=" dark:bg-darkmodeColor  shadow-md dark:shadow-custom-dark p-4 rounded-custom border-l-4 border-indigo-500">
                        <h3 className="text-sm font-semibold mb-2 text-indigo-700 dark:text-indigo-300 flex items-center">
                            <FaBoxOpen className="mr-2" />
                            Product Details
                        </h3>

                        <div className="grid grid-cols-1 gap-6">
                            <div className=" ">
                                {renderField('Products Description', 'products_desc')}
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-1">
                        </div>
                    </div>

                    {/* Pickup Information Section */}
                    <div className=" dark:bg-darkmodeColor  shadow-md dark:shadow-custom-dark rounded-custom p-4 border-l-4 border-red-500">
                        <h3 className="text-sm font-semibold mb-4 text-red-700 dark:text-red-300 flex items-center">
                            <FaTruck className="mr-2" />
                            Pickup Information
                        </h3>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className=" ">
                                {renderField('Pickup Name', 'pickup_name')}
                            </div>
                            <div className=" ">
                                {renderField('Pickup Address', 'pickup_address')}
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-1">
                            <div className=" ">
                                {renderField('Pickup City', 'pickup_city')}
                            </div>
                            <div className=" ">
                                {renderField('Pickup Pin Code', 'pickup_pin_code')}
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-1">
                            {/* <div className=" ">
                                {renderField('Pickup Country', 'pickup_country')}
                            </div> */}
                            <div className=" ">
                                {renderField('Pickup Phone', 'pickup_phone')}
                            </div>
                            <div className=" ">
                                {renderField('Address Type', 'address_type')}
                            </div>
                        </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex flex-wrap justify-end gap-4 mt-1">
                        <button
                            type="button"
                            onClick={handleClose}
                            className="w-full sm:w-auto bg-gray-500 text-white py-3 px-6 rounded-lg hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-opacity-50 font-medium transition-colors flex items-center justify-center"
                        >
                            <FaTimes className="mr-2" /> Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={isLoading && isEditing}
                            className={`
                                    w-full sm:w-auto  nm 
                                    bg-gradient-to-r from-purple-600 to-indigo-700 
                                    text-white py-3 px-6 rounded-lg 
                                    hover:from-purple-700 hover:to-indigo-800 
                                    focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-opacity-50 
                                    font-medium transition-colors flex items-center justify-center shadow-md
                                    disabled:from-gray-400 disabled:to-gray-500 disabled:cursor-not-allowed
                                `}>
                            {isLoading && isEditing ? (
                                <>
                                    <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    Saving...
                                </>
                            ) : (
                                <>
                                    <FaSave className="mr-2" /> Save Changes
                                </>
                            )}
                        </button>
                    </div>

                </form>
                {/* </div> */}
            </DialogContent>

        </Dialog>

    );
};

export default ViewAllShipment;
